import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Network } from '@capacitor/network';
import { NativeMarket } from '@capacitor-community/native-market';

import { PlatformService } from 'app/core/services/platform.service';
import { GeneralModalAnimation } from 'app/animations/general-modal-animation';
import { ModalAppAvailabilityComponent } from './modal-app-availability/modal-app-availability.component';

export interface ModalOptioins {
  component: any;
  cssClass: any;
  componentProps?: any;
  enterAnimation?: any;
  leaveAnimation?: any;
  backdropDismiss?: boolean;
  hideAfterRedirect?: boolean;
}

export const AppLinks = {
  ANDROID: 'https://play.google.com/store/apps/details?id=com.sonymusic.promoportal',
  IOS: 'https://apps.apple.com/app/sony-music-promo-portal/id1466205488',
};

@Injectable({ providedIn: 'root' })
export class ModalsService {
  private openModalCount = 0;
  private hideAfterRedirect = true;

  constructor(
    private modalController: ModalController,
    private router: Router,
    private platform: Platform,
    private platformService: PlatformService,
    private animation: GeneralModalAnimation,
  ) {
    this.listenRouter();
    this.listenOnDisconnect();
  }

  async showModal(options: ModalOptioins): Promise<any> {
    const { component, cssClass, componentProps, enterAnimation, leaveAnimation } = options;
    const backdropDismiss = options.backdropDismiss ?? true;
    if (this.openModalCount) {
      await this.closeModal();
    }
    const modal = await this.modalController.create({
      component,
      cssClass,
      enterAnimation,
      leaveAnimation,
      componentProps,
      backdropDismiss,
    });
    this.openModalCount += 1;
    this.hideAfterRedirect = options.hideAfterRedirect ?? true;
    await modal.present();

    return await modal.onDidDismiss().then(() => {
      this.hideAfterRedirect = true;
      this.openModalCount -= 1;
    });
  }

  async closeModal() {
    if (this.openModalCount && this.hideAfterRedirect) {
      return await this.modalController?.dismiss();
    }

    return Promise.resolve(true);
  }

  checkAppAvailability() {
    let platformName = '';
    let platformLink = '';

    if (this.platformService.isDesktop()) {
      return;
    }

    if (this.platformService.isAndroidMobileBrowser()) {
      platformName = 'Android';
      platformLink = AppLinks.ANDROID;
    } else if (this.platformService.isIOSMobileBrowser()) {
      platformName = 'IOS';
      platformLink = AppLinks.IOS;
    }

    if (platformLink && platformName) {
      this.showModal({
        component: ModalAppAvailabilityComponent,
        cssClass: 'modal-app-availability',
        enterAnimation: this.animation.getAnimations().enterAnimation,
        leaveAnimation: this.animation.getAnimations().leaveAnimation,
        componentProps: {
          platformName,
          platformLink,
        },
        hideAfterRedirect: false,
      });
    }
  }

  openUpdateLink() {
    const appId = this.isIos() ? 'ios.sonymusic.promoportal' : 'com.sonymusic.promoportal';
    NativeMarket.openStoreListing({ appId });
  }

  private listenRouter() {
    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationStart))
      .subscribe(() => {
        this.closeModal();
      });
  }

  private listenOnDisconnect() {
    this.platform.ready().then(() => {
      if (this.platformService.isCordova()) {
        Network.addListener('networkStatusChange', ({ connected }) => {
          if (!connected) {
            this.closeModal();
          }
        });
      }
    });
  }

  private isIos(): boolean {
    return this.platform.is('ios');
  }
}
